import React from 'react';
import { Mui, MuiDataGrid } from '@leap4/frontend';

/**
 * The grid's toolbar - Columns, Filters, Download as CSV - plus an "Active filters [N]" chip on
 * the left whenever a filter is on. Filter models are saved per browser, so a filter set weeks ago
 * still hides rows, and the Filters button's dot alone was too easy to miss. Clicking the chip
 * opens the filter panel.
 *
 * The count comes from the grid's own active-filter selector - the same one behind the dot - so
 * half-filled filter rows don't count and the two never disagree.
 *
 *   <DataGridPro showToolbar slots={{ toolbar: FiltersToolbar }}
 *       slotProps={{ toolbar: { csvOptions: { fileName: 'publishers' } } }} ... />
 */
export const FiltersToolbar: React.FC<{ csvOptions?: MuiDataGrid.GridCsvExportOptions }> = ({ csvOptions }) => {
    const {
        Toolbar,
        ToolbarButton,
        ColumnsPanelTrigger,
        FilterPanelTrigger,
        ExportCsv,
        useGridApiContext,
        useGridRootProps,
        useGridSelector,
        gridFilterActiveItemsSelector,
    } = MuiDataGrid;
    const apiRef = useGridApiContext();
    const { slots } = useGridRootProps();
    const activeCount = useGridSelector(apiRef, gridFilterActiveItemsSelector).length;
    const Tooltip = slots.baseTooltip;
    const t = (key: string) => apiRef.current.getLocaleText(key as any);

    return (
        <Toolbar>
            {activeCount > 0 && (
                <Mui.Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto', px: 0.5, typography: 'body2', color: 'text.secondary' }}
                >
                    Active filters
                    <Mui.Chip
                        size="small"
                        color="primary"
                        label={activeCount}
                        onClick={() => apiRef.current.showFilterPanel()}
                        aria-label={`${activeCount} active filters - open filter panel`}
                    />
                </Mui.Box>
            )}
            <Tooltip title={t('toolbarColumns')}>
                <ColumnsPanelTrigger render={<ToolbarButton />}>
                    <slots.columnSelectorIcon fontSize="small" />
                </ColumnsPanelTrigger>
            </Tooltip>
            <Tooltip title={t('toolbarFilters')}>
                <FilterPanelTrigger
                    render={(triggerProps, state) => (
                        <ToolbarButton {...triggerProps} color={state.filterCount > 0 ? 'primary' : 'default'}>
                            <slots.baseBadge badgeContent={state.filterCount} color="primary" variant="dot">
                                <slots.openFilterButtonIcon fontSize="small" />
                            </slots.baseBadge>
                        </ToolbarButton>
                    )}
                />
            </Tooltip>
            <Mui.Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />
            <Tooltip title={t('toolbarExportCSV')}>
                <ExportCsv options={csvOptions} render={<ToolbarButton />}>
                    <slots.exportIcon fontSize="small" />
                </ExportCsv>
            </Tooltip>
        </Toolbar>
    );
};
